import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Lock, LogOut, Swords, X, Loader2, Users, Bot } from "lucide-react";
import {
    socket,
    API_URL,
    getStoredAuth,
    setStoredAuth,
    clearStoredAuth,
    refreshSocketAuth,
} from "../socket";
import Tutorial from "./Tutorial/TutorialOverlay";

/**
 * Login / matchmaking drawer.
 *
 * Slides in from the right when the user clicks the JOIN BATTLE CTA in the
 * top bar. All prior auth logic is preserved verbatim; only the presentation
 * changed (center card → right-side poster drawer, purple chrome → cream on
 * red with amber accents).
 *
 * Owns:
 *   - username input (controlled via props.username / props.setUsername)
 *   - optional password input (revealed when the server says the name is
 *     registered, or when the user opts to register)
 *   - the in-flight matchmaking spinner (isLoading)
 *   - the tutorial modal (so the "How to play?" link inside the drawer can
 *     still trigger it even if the parent doesn't wire the top-bar link)
 *
 * Parent owns the drawer's open/close state (props.isOpen / props.onClose)
 * and the running username value. This keeps App.js in charge of routing
 * (e.g. auto-closing the drawer when matchmaking succeeds).
 *
 * Optional: props.preferredMode = 'online' | 'solo' — when opening from
 * "Play vs AI" on the home page, the solo section is shown first and reads
 * as a separate path from the online login form.
 */

// ------------ animations ---------------------------------------------------

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ------------ chrome -------------------------------------------------------

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: radial-gradient(
    circle at 70% 40%,
    rgba(58, 10, 16, 0.4) 0%,
    rgba(8, 7, 10, 0.8) 100%
  );
  backdrop-filter: blur(3px);
`;

const Drawer = styled(motion.aside)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 440px;
  max-width: 100vw;
  z-index: 1201;
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    180deg,
    var(--color-red-800) 0%,
    var(--color-red-900) 100%
  );
  box-shadow: var(--shadow-xl);
  /* diagonal cut on the bottom-left so the drawer reads as a poster card
     that's been slid onto the screen (matches --clip-card bottom-right but
     mirrored to the left since this hugs the right edge). */
  clip-path: polygon(0 0, 100% 0, 100% 100%, 24px 100%, 0 calc(100% - 24px));
  overflow: hidden;

  /* local grain overlay for the drawer surface so it doesn't read as a flat
     red rectangle under the global grain layer */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: var(--grain-url);
    background-repeat: repeat;
    opacity: 0.12;
    mix-blend-mode: overlay;
    pointer-events: none;
    z-index: 0;
  }

  @media (max-width: 480px) {
    width: 100vw;
    clip-path: none;
  }
`;

const DrawerInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--space-8) var(--space-6) var(--space-6);
`;

const CloseButton = styled.button`
  position: absolute;
  top: var(--space-4);
  right: var(--space-4);
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid rgba(246, 241, 228, 0.25);
  background: transparent;
  color: var(--color-cream-200);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out),
              border-color var(--dur-fast) var(--ease-out);

  &:hover,
  &:focus-visible {
    color: var(--color-cream-100);
    border-color: var(--color-cream-100);
    outline: none;
  }
`;

const Header = styled.header`
  margin-bottom: var(--space-6);
`;

const Eyebrow = styled.div`
  font-family: var(--font-heading);
  font-weight: var(--weight-semibold);
  font-size: var(--size-xs);
  color: var(--color-amber-500);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  margin-bottom: var(--space-2);
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 20px;
    height: 2px;
    background: var(--color-amber-500);
  }
`;

const Title = styled.h2`
  font-family: var(--font-display);
  font-size: var(--size-3xl);
  font-weight: var(--weight-regular);
  letter-spacing: var(--tracking-tight);
  color: var(--color-cream-100);
  line-height: var(--line-height-tight, 1.05);
  text-transform: uppercase;
  margin: 0 0 var(--space-2) 0;
`;

const Subtitle = styled.p`
  font-family: var(--font-body);
  font-size: var(--size-sm);
  color: var(--color-cream-300);
  line-height: 1.5;
  margin: 0;
`;

// ------------ form fields --------------------------------------------------

const FormGroup = styled.div`
  margin-bottom: var(--space-5);
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-heading);
  font-size: var(--size-xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  color: var(--color-cream-200);
  margin-bottom: var(--space-2);

  svg {
    stroke-width: 2.5;
  }
`;

const InputWrap = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  background: rgba(8, 7, 10, 0.35);
  border: 1px solid rgba(246, 241, 228, 0.18);
  border-left: 3px solid
    ${p => (p.$invalid ? 'var(--color-danger)' : 'var(--color-amber-500)')};
  color: var(--color-cream-100);
  font-family: var(--font-body);
  font-size: var(--size-base);
  font-weight: var(--weight-medium);
  letter-spacing: 0.01em;
  transition: border-color var(--dur-fast) var(--ease-out),
              background var(--dur-fast) var(--ease-out);
  outline: none;

  &::placeholder {
    color: var(--color-cream-300);
    opacity: 0.55;
  }

  &:focus {
    background: rgba(8, 7, 10, 0.5);
    border-color: rgba(246, 241, 228, 0.35);
    border-left-color: var(--color-amber-400);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HelperText = styled.div`
  font-family: var(--font-body);
  font-size: var(--size-xs);
  color: ${p => (p.$error ? 'var(--color-danger)' : 'var(--color-cream-300)')};
  margin-top: var(--space-2);
  line-height: 1.45;
`;

const Select = styled.select`
  width: 100%;
  padding: 14px 16px;
  background: rgba(8, 7, 10, 0.35);
  border: 1px solid rgba(246, 241, 228, 0.18);
  border-left: 3px solid var(--color-amber-500);
  color: var(--color-cream-100);
  font-family: var(--font-body);
  font-size: var(--size-base);
  font-weight: var(--weight-medium);
  letter-spacing: 0.01em;
  outline: none;
  transition: border-color var(--dur-fast) var(--ease-out),
              background var(--dur-fast) var(--ease-out);

  &:focus {
    background: rgba(8, 7, 10, 0.5);
    border-color: rgba(246, 241, 228, 0.35);
    border-left-color: var(--color-amber-400);
  }

  option {
    background: var(--color-ink-950);
    color: var(--color-cream-100);
  }
`;

// ------------ CTA ----------------------------------------------------------

const SubmitButton = styled.button`
  position: relative;
  width: 100%;
  margin-top: var(--space-4);
  padding: 16px 20px;
  background: var(--color-amber-500);
  color: var(--color-ink-950);
  border: none;
  clip-path: var(--clip-button);
  font-family: var(--font-display);
  font-size: var(--size-lg);
  font-weight: var(--weight-regular);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background var(--dur-base) var(--ease-out),
              transform var(--dur-base) var(--ease-out),
              box-shadow var(--dur-base) var(--ease-out);

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: var(--color-amber-400);
    transform: translateY(-1px);
    box-shadow: var(--shadow-glow-amber);
    outline: none;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: var(--color-ink-500);
    color: var(--color-cream-300);
    cursor: not-allowed;
  }
`;

const SpinnerIcon = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

const TutorialLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: var(--space-5) auto 0;
  padding: var(--space-2) var(--space-3);
  background: transparent;
  border: 1px dashed rgba(246, 241, 228, 0.25);
  color: var(--color-cream-200);
  font-family: var(--font-heading);
  font-size: var(--size-xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out),
              border-color var(--dur-fast) var(--ease-out);

  &:hover,
  &:focus-visible {
    color: var(--color-cream-100);
    border-color: var(--color-cream-100);
    outline: none;
  }
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: rgba(246, 241, 228, 0.14);
  margin: var(--space-4) 0;
`;

const SectionOr = styled.p`
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: center;
  color: var(--color-cream-400);
  margin: var(--space-3) 0;
`;

const SessionBanner = styled.div`
  padding: var(--space-4) var(--space-4);
  margin-bottom: var(--space-5);
  background: rgba(8, 7, 10, 0.45);
  border: 1px solid rgba(246, 241, 228, 0.18);
  border-left: 3px solid var(--color-amber-500);
  clip-path: var(--clip-card);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const SessionTitle = styled.div`
  font-family: var(--font-heading);
  font-size: var(--size-xs);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-amber-500);
`;

const SessionName = styled.div`
  font-family: var(--font-body);
  font-size: var(--size-base);
  font-weight: var(--weight-semibold);
  color: var(--color-cream-100);
`;

const SessionHint = styled.p`
  margin: 0;
  font-family: var(--font-body);
  font-size: var(--size-xs);
  color: var(--color-cream-300);
  line-height: 1.45;
`;

const ModeSection = styled.section`
  margin-bottom: var(--space-5);
  padding: var(--space-4) var(--space-4);
  background: ${p => (p.$variant === "solo" ? "rgba(8, 7, 10, 0.38)" : "rgba(8, 7, 10, 0.28)")};
  border: 1px solid
    ${p => (p.$variant === "solo" ? "rgba(103, 201, 122, 0.35)" : "rgba(246, 241, 228, 0.14)")};
  clip-path: var(--clip-card);
`;

const ModeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-heading);
  font-size: 10px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: ${p => (p.$variant === "solo" ? "var(--color-success)" : "var(--color-cream-200)")};
  margin-bottom: var(--space-2);

  svg {
    opacity: 0.9;
  }
`;

const ModeBlurb = styled.p`
  margin: 0 0 var(--space-3) 0;
  font-family: var(--font-body);
  font-size: var(--size-xs);
  color: var(--color-cream-300);
  line-height: 1.45;
`;

const SoloActionButton = styled.button`
  position: relative;
  width: 100%;
  margin-top: var(--space-2);
  padding: 14px 18px;
  background: var(--color-ink-800);
  color: var(--color-cream-100);
  border: 1px solid rgba(103, 201, 122, 0.4);
  clip-path: var(--clip-button);
  font-family: var(--font-display);
  font-size: var(--size-base);
  font-weight: var(--weight-regular);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background var(--dur-base) var(--ease-out),
    transform var(--dur-base) var(--ease-out);

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: var(--color-ink-700);
    transform: translateY(-1px);
    outline: none;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const TertiaryLink = styled.button`
  display: block;
  width: 100%;
  margin-top: var(--space-2);
  padding: 0;
  background: none;
  border: none;
  color: var(--color-cream-400);
  font-family: var(--font-body);
  font-size: var(--size-xs);
  text-align: left;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: var(--color-cream-200);
  }
`;

// ------------ loading overlay (matchmaking) --------------------------------

const MatchSearching = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: var(--space-4);
  padding: var(--space-8);
  text-align: center;
`;

const MatchSearchingRing = styled.div`
  width: 72px;
  height: 72px;
  border: 3px solid rgba(246, 241, 228, 0.15);
  border-top-color: var(--color-amber-500);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const MatchSearchingText = styled.div`
  font-family: var(--font-display);
  font-size: var(--size-2xl);
  letter-spacing: var(--tracking-tight);
  color: var(--color-cream-100);
  text-transform: uppercase;
`;

const MatchSearchingHint = styled.div`
  font-family: var(--font-body);
  font-size: var(--size-sm);
  color: var(--color-cream-300);
  max-width: 28ch;
`;

// ------------ component ----------------------------------------------------

export const MyForm = (props) => {
    const {
        isOpen = true,
        onClose,
        openTutorialExternally, // optional: parent-owned tutorial trigger
        preferredMode = "online", // 'online' | 'solo'
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [error, setError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [password, setPassword] = useState("");
    const [passwordRequired, setPasswordRequired] = useState(false);
    const [showTutorial, setShowTutorial] = useState(false);
    const [authToken, setAuthToken] = useState(() => getStoredAuth().token);
    const [isReturningUser, setIsReturningUser] = useState(() => !!getStoredAuth().token);
    const [aiDifficulty, setAiDifficulty] = useState("normal");
    /** Which flow is running while the full-screen "searching" overlay shows */
    const [queueMode, setQueueMode] = useState("online");
    /** When signed in, hide username/password until user asks to change account */
    const [showAccountForm, setShowAccountForm] = useState(false);

    const usernameInputRef = useRef(null);
    const passwordInputRef = useRef(null);

    const sessionSnapshot = getStoredAuth();
    const isLoggedIn = !!(authToken && sessionSnapshot.token && sessionSnapshot.username);

    // Pre-populate username from stored session; re-sync when drawer opens so
    // the player stays "always logged in" for this device without re-typing.
    useEffect(() => {
        if (!isOpen) return;
        const stored = getStoredAuth();
        if (stored.token) {
            setAuthToken(stored.token);
            setIsReturningUser(true);
        } else {
            setIsReturningUser(false);
        }
        if (stored.username) {
            props.setUsername(stored.username);
        }
        if (stored.token && stored.username) {
            setShowAccountForm(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Focus the username field when the drawer opens (only if we show the
    // sign-in / name fields).
    useEffect(() => {
        if (!isOpen) return;
        if (isLoggedIn && !showAccountForm) return;
        if (usernameInputRef.current && !isLoading) {
            const t = setTimeout(() => {
                usernameInputRef.current?.focus();
            }, 280);
            return () => clearTimeout(t);
        }
    }, [isOpen, isLoading, isLoggedIn, showAccountForm]);

    // First-time visitors see the tutorial automatically once the drawer
    // is open (matches prior behavior).
    useEffect(() => {
        if (!isOpen) return;
        const hasSeenTutorial = localStorage.getItem('movie-mayhem-tutorial-seen');
        if (!hasSeenTutorial) {
            const t = setTimeout(() => setShowTutorial(true), 900);
            return () => clearTimeout(t);
        }
    }, [isOpen]);

    // Focus password field when the server reveals it.
    useEffect(() => {
        if (passwordRequired && passwordInputRef.current) {
            passwordInputRef.current.focus();
        }
    }, [passwordRequired]);

    // Close drawer on Escape (but not while matchmaking, to avoid accidents).
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape' && !isLoading && onClose) {
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, isLoading, onClose]);

    const onLogout = () => {
        const { token } = getStoredAuth();
        clearStoredAuth();
        setAuthToken(null);
        setIsReturningUser(false);
        setPassword("");
        setPasswordRequired(false);
        setError("");
        setPasswordError("");
        setShowAccountForm(false);
        props.setUsername("");
        if (token) {
            fetch(`${API_URL}/api/auth/logout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            }).catch(() => { });
        }
        refreshSocketAuth();
    };

    const startMatchmaking = (token) => {
        setQueueMode("online");
        setIsLoading(true);
        socket.emit(
            "matchup",
            { username: props.username.trim(), token },
            (ack) => {
                if (ack && ack.error) {
                    setIsLoading(false);
                    if (ack.requiresLogin) {
                        clearStoredAuth();
                        setAuthToken(null);
                        setIsReturningUser(false);
                        setPasswordRequired(true);
                        setError('Your session expired. Please log in again.');
                    } else {
                        setError(ack.error);
                    }
                    return;
                }
                // The 'game start' socket event handled at the App level will
                // take over here; keep the spinner on until then.
            }
        );
    };

    const startSoloVsAi = (token) => {
        setQueueMode("solo");
        setIsLoading(true);
        socket.emit(
            "solo_matchup",
            { username: props.username.trim(), token, difficulty: aiDifficulty },
            (ack) => {
                if (ack && ack.error) {
                    setIsLoading(false);
                    if (ack.requiresLogin) {
                        clearStoredAuth();
                        setAuthToken(null);
                        setIsReturningUser(false);
                        setPasswordRequired(true);
                        setError('Your session expired. Please log in again.');
                    } else {
                        setError(ack.error);
                    }
                    return;
                }
                // 'game start' will take over at the App level.
            }
        );
    };

    const onSubmit = async (e) => {
        e?.preventDefault?.();
        const username = props.username.trim();
        setError("");
        setPasswordError("");

        if (!username) {
            setError("Please enter your battle name");
            return;
        }

        // Fast path: reuse existing valid session
        if (authToken && getStoredAuth().username === username) {
            startMatchmaking(authToken);
            return;
        }

        setIsAuthenticating(true);
        try {
            const res = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password: password || undefined }),
            });
            const data = await res.json();

            if (!res.ok) {
                if (data.requiresPassword) {
                    setPasswordRequired(true);
                    if (password) {
                        setPasswordError(data.error || 'Incorrect password');
                    } else {
                        setError(data.error || 'This name is registered - enter its password.');
                    }
                } else {
                    setError(data.error || 'Login failed');
                }
                return;
            }

            setStoredAuth({ token: data.token, username: data.username });
            setAuthToken(data.token);
            setIsReturningUser(true);
            props.setUsername(data.username);

            // Reconnect socket under the new token, THEN start matchmaking.
            const onConnected = () => {
                socket.off('connect', onConnected);
                startMatchmaking(data.token);
            };
            socket.on('connect', onConnected);
            refreshSocketAuth();
        } catch (err) {
            console.error('Login request failed:', err);
            setError('Could not reach the server. Check your connection.');
        } finally {
            setIsAuthenticating(false);
        }
    };

    /** For “Use a different battle name” — sign in or register without starting a match. */
    const onSaveAccountOnly = async (e) => {
        e?.preventDefault?.();
        const username = props.username.trim();
        setError("");
        setPasswordError("");

        if (!username) {
            setError("Please enter a battle name");
            return;
        }

        if (authToken && getStoredAuth().username === username) {
            setShowAccountForm(false);
            return;
        }

        setIsAuthenticating(true);
        try {
            const res = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password: password || undefined }),
            });
            const data = await res.json();

            if (!res.ok) {
                if (data.requiresPassword) {
                    setPasswordRequired(true);
                    if (password) {
                        setPasswordError(data.error || 'Incorrect password');
                    } else {
                        setError(data.error || 'Enter the password for this name.');
                    }
                } else {
                    setError(data.error || 'Login failed');
                }
                return;
            }

            setStoredAuth({ token: data.token, username: data.username });
            setAuthToken(data.token);
            setIsReturningUser(true);
            setShowAccountForm(false);
            setPassword("");
            props.setUsername(data.username);
            refreshSocketAuth();
        } catch (err) {
            console.error('Login request failed:', err);
            setError('Could not reach the server. Check your connection.');
        } finally {
            setIsAuthenticating(false);
        }
    };

    /** Shared by the solo button and any legacy submit handler. */
    const beginSoloMatch = async () => {
        const username = props.username.trim();
        setError("");
        setPasswordError("");

        if (!username) {
            setError("Enter your battle name above first.");
            return;
        }

        if (authToken && getStoredAuth().username === username) {
            startSoloVsAi(authToken);
            return;
        }

        setIsAuthenticating(true);
        try {
            const res = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password: password || undefined }),
            });
            const data = await res.json();

            if (!res.ok) {
                if (data.requiresPassword) {
                    setPasswordRequired(true);
                    if (password) {
                        setPasswordError(data.error || 'Incorrect password');
                    } else {
                        setError(data.error || 'This name is registered - enter its password in Online.');
                    }
                } else {
                    setError(data.error || 'Login failed');
                }
                return;
            }

            setStoredAuth({ token: data.token, username: data.username });
            setAuthToken(data.token);
            setIsReturningUser(true);
            props.setUsername(data.username);

            const onConnected = () => {
                socket.off('connect', onConnected);
                startSoloVsAi(data.token);
            };
            socket.on('connect', onConnected);
            refreshSocketAuth();
        } catch (err) {
            console.error('Login request failed:', err);
            setError('Could not reach the server. Check your connection.');
        } finally {
            setIsAuthenticating(false);
        }
    };

    const openTutorial = () => {
        if (openTutorialExternally) {
            openTutorialExternally();
        } else {
            setShowTutorial(true);
        }
    };

    // --------- render ------------------------------------------------------

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <>
                        <Backdrop
                            key="backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.18 }}
                            onClick={() => !isLoading && onClose?.()}
                            aria-hidden="true"
                        />
                        <Drawer
                            key="drawer"
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                            role="dialog"
                            aria-label="Join the battle"
                            aria-modal="true"
                        >
                            <DrawerInner>
                                {onClose && !isLoading && (
                                    <CloseButton
                                        type="button"
                                        onClick={onClose}
                                        aria-label="Close"
                                    >
                                        <X size={18} />
                                    </CloseButton>
                                )}

                                {isLoading ? (
                                    <MatchSearching>
                                        <MatchSearchingRing aria-hidden="true" />
                                        <MatchSearchingText>
                                            {queueMode === "solo" ? "Starting solo match" : "Finding a match"}
                                        </MatchSearchingText>
                                        <MatchSearchingHint>
                                            {queueMode === "solo"
                                                ? "Your teams and the AI are loading. This does not change your rating."
                                                : "Pairing you with another player. Random fighters load when the arena opens."}
                                        </MatchSearchingHint>
                                    </MatchSearching>
                                ) : (
                                    <>
                                        <Header>
                                            <Eyebrow>Ready to Battle</Eyebrow>
                                            <Title>Enter the Arena</Title>
                                            <Subtitle>
                                                {isLoggedIn
                                                    ? "You stay signed in on this device. Choose online PvP or solo practice (rating unchanged for solo)."
                                                    : "Enter a battle name once. Online uses your password when needed; solo is a separate path below."}
                                            </Subtitle>
                                        </Header>

                                        {isLoggedIn && !showAccountForm && (
                                            <SessionBanner>
                                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                                                    <div>
                                                        <SessionTitle>Signed in</SessionTitle>
                                                        <SessionName>{getStoredAuth().username}</SessionName>
                                                        <SessionHint>
                                                            Session saved on this browser until you sign out.
                                                        </SessionHint>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={onLogout}
                                                        style={{
                                                            background: "transparent",
                                                            border: "1px solid rgba(246, 241, 228, 0.25)",
                                                            color: "var(--color-cream-200)",
                                                            padding: "6px 12px",
                                                            fontSize: 10,
                                                            fontFamily: "var(--font-heading)",
                                                            textTransform: "uppercase",
                                                            letterSpacing: "0.08em",
                                                            cursor: "pointer",
                                                            clipPath: "var(--clip-button)",
                                                        }}
                                                    >
                                                        <LogOut size={12} style={{ marginRight: 6, verticalAlign: "middle" }} />
                                                        Sign out
                                                    </button>
                                                </div>
                                            </SessionBanner>
                                        )}

                                        {isLoggedIn && showAccountForm && (
                                            <form onSubmit={onSaveAccountOnly} noValidate>
                                                <FormGroup>
                                                    <Label htmlFor="mm-username">Battle name</Label>
                                                    <InputWrap>
                                                        <Input
                                                            id="mm-username"
                                                            className="username-input"
                                                            ref={usernameInputRef}
                                                            type="text"
                                                            value={props.username}
                                                            autoComplete="username"
                                                            onChange={(e) => {
                                                                props.setUsername(e.target.value);
                                                                if (passwordRequired) {
                                                                    setPasswordRequired(false);
                                                                    setPassword("");
                                                                    setPasswordError("");
                                                                }
                                                                if (error) setError("");
                                                            }}
                                                            $invalid={!!error}
                                                            disabled={isAuthenticating}
                                                        />
                                                    </InputWrap>
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label htmlFor="mm-password">
                                                        <Lock size={12} />
                                                        Password
                                                    </Label>
                                                    <InputWrap>
                                                        <Input
                                                            id="mm-password"
                                                            className="password-input"
                                                            ref={passwordInputRef}
                                                            type="password"
                                                            autoComplete={passwordRequired ? "current-password" : "new-password"}
                                                            value={password}
                                                            onChange={(e) => {
                                                                setPassword(e.target.value);
                                                                if (passwordError) setPasswordError("");
                                                            }}
                                                            $invalid={!!passwordError}
                                                            disabled={isAuthenticating}
                                                        />
                                                    </InputWrap>
                                                </FormGroup>
                                                {error && <HelperText $error>{error}</HelperText>}
                                                {passwordError && <HelperText $error>{passwordError}</HelperText>}
                                                <SubmitButton type="submit" disabled={isAuthenticating} style={{ marginTop: 0 }}>
                                                    {isAuthenticating ? <><SpinnerIcon size={18} /> Saving…</> : "Save and continue"}
                                                </SubmitButton>
                                                <TertiaryLink type="button" onClick={() => { setShowAccountForm(false); setError(""); setPasswordError(""); }}>
                                                    Back
                                                </TertiaryLink>
                                            </form>
                                        )}

                                        {!isLoggedIn && (
                                            <>
                                                <FormGroup>
                                                    <Label htmlFor="mm-username">Your battle name</Label>
                                                    <InputWrap>
                                                        <Input
                                                            id="mm-username"
                                                            className="username-input"
                                                            ref={usernameInputRef}
                                                            type="text"
                                                            placeholder="e.g. MadrasBoss"
                                                            value={props.username}
                                                            autoComplete="username"
                                                            onChange={(e) => {
                                                                props.setUsername(e.target.value);
                                                                if (passwordRequired) {
                                                                    setPasswordRequired(false);
                                                                    setPassword("");
                                                                    setPasswordError("");
                                                                }
                                                                if (error) setError("");
                                                            }}
                                                            $invalid={!!error}
                                                            disabled={isAuthenticating}
                                                        />
                                                    </InputWrap>
                                                    {error && <HelperText $error>{error}</HelperText>}
                                                </FormGroup>

                                                {preferredMode === "solo" ? (
                                                    <>
                                                        <ModeSection $variant="solo" aria-label="Solo vs AI">
                                                            <ModeLabel $variant="solo">
                                                                <Bot size={14} aria-hidden="true" />
                                                                Solo / Practice
                                                            </ModeLabel>
                                                            <ModeBlurb>
                                                                Instant game vs the AI. No queue. Your rating is unchanged.
                                                            </ModeBlurb>
                                                            <FormGroup>
                                                                <Label htmlFor="mm-ai-difficulty-guest">AI difficulty</Label>
                                                                <InputWrap>
                                                                    <Select
                                                                        id="mm-ai-difficulty-guest"
                                                                        value={aiDifficulty}
                                                                        onChange={(e) => setAiDifficulty(e.target.value)}
                                                                        disabled={isAuthenticating}
                                                                    >
                                                                        <option value="easy">Easy (training)</option>
                                                                        <option value="normal">Normal</option>
                                                                        <option value="hard">Hard</option>
                                                                    </Select>
                                                                </InputWrap>
                                                            </FormGroup>
                                                            <SoloActionButton
                                                                type="button"
                                                                onClick={beginSoloMatch}
                                                                disabled={isAuthenticating}
                                                                aria-label="Start solo match versus AI"
                                                            >
                                                                {isAuthenticating ? (
                                                                    <>
                                                                        <SpinnerIcon size={18} />
                                                                        Signing in
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Bot size={18} />
                                                                        Play vs AI
                                                                    </>
                                                                )}
                                                            </SoloActionButton>
                                                        </ModeSection>
                                                        <SectionOr>or</SectionOr>
                                                        <form onSubmit={onSubmit} noValidate>
                                                            <ModeSection $variant="online" aria-label="Online matchmaking">
                                                                <ModeLabel $variant="online">
                                                                    <Users size={14} aria-hidden="true" />
                                                                    Online
                                                                </ModeLabel>
                                                                <ModeBlurb>
                                                                    Queue for a match vs another human (rated). Password only if this name is registered.
                                                                </ModeBlurb>
                                                                <FormGroup>
                                                                    <Label htmlFor="mm-password">
                                                                        <Lock size={12} />
                                                                        Password {passwordRequired ? "" : "(optional)"}
                                                                    </Label>
                                                                    <InputWrap>
                                                                        <Input
                                                                            id="mm-password"
                                                                            className="password-input"
                                                                            ref={passwordInputRef}
                                                                            type="password"
                                                                            autoComplete={passwordRequired ? "current-password" : "new-password"}
                                                                            placeholder={
                                                                                passwordRequired
                                                                                    ? "Enter your password"
                                                                                    : "Set one to protect this name"
                                                                            }
                                                                            value={password}
                                                                            onChange={(e) => {
                                                                                setPassword(e.target.value);
                                                                                if (passwordError) setPasswordError("");
                                                                            }}
                                                                            $invalid={!!passwordError}
                                                                            disabled={isAuthenticating}
                                                                        />
                                                                    </InputWrap>
                                                                    {passwordError ? (
                                                                        <HelperText $error>{passwordError}</HelperText>
                                                                    ) : (
                                                                        <HelperText>
                                                                            {passwordRequired
                                                                                ? "This name is taken — enter its password."
                                                                                : "Optional: claim this name for future logins."}
                                                                        </HelperText>
                                                                    )}
                                                                </FormGroup>
                                                                <SubmitButton
                                                                    className="find-match-button"
                                                                    type="submit"
                                                                    disabled={isAuthenticating}
                                                                    style={{ marginTop: 0 }}
                                                                >
                                                                    {isAuthenticating ? (
                                                                        <>
                                                                            <SpinnerIcon size={18} />
                                                                            Signing in
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Swords size={18} />
                                                                            Find online match
                                                                        </>
                                                                    )}
                                                                </SubmitButton>
                                                            </ModeSection>
                                                        </form>
                                                    </>
                                                ) : (
                                                    <>
                                                        <form onSubmit={onSubmit} noValidate>
                                                            <ModeSection $variant="online" aria-label="Online matchmaking">
                                                                <ModeLabel $variant="online">
                                                                    <Users size={14} aria-hidden="true" />
                                                                    Online
                                                                </ModeLabel>
                                                                <ModeBlurb>
                                                                    Queue for a match vs another human (rated). Password only if this name is registered.
                                                                </ModeBlurb>
                                                                <FormGroup>
                                                                    <Label htmlFor="mm-password">
                                                                        <Lock size={12} />
                                                                        Password {passwordRequired ? "" : "(optional)"}
                                                                    </Label>
                                                                    <InputWrap>
                                                                        <Input
                                                                            id="mm-password"
                                                                            className="password-input"
                                                                            ref={passwordInputRef}
                                                                            type="password"
                                                                            autoComplete={passwordRequired ? "current-password" : "new-password"}
                                                                            placeholder={
                                                                                passwordRequired
                                                                                    ? "Enter your password"
                                                                                    : "Set one to protect this name"
                                                                            }
                                                                            value={password}
                                                                            onChange={(e) => {
                                                                                setPassword(e.target.value);
                                                                                if (passwordError) setPasswordError("");
                                                                            }}
                                                                            $invalid={!!passwordError}
                                                                            disabled={isAuthenticating}
                                                                        />
                                                                    </InputWrap>
                                                                    {passwordError ? (
                                                                        <HelperText $error>{passwordError}</HelperText>
                                                                    ) : (
                                                                        <HelperText>
                                                                            {passwordRequired
                                                                                ? "This name is taken — enter its password."
                                                                                : "Optional: claim this name for future logins."}
                                                                        </HelperText>
                                                                    )}
                                                                </FormGroup>
                                                                <SubmitButton
                                                                    className="find-match-button"
                                                                    type="submit"
                                                                    disabled={isAuthenticating}
                                                                    style={{ marginTop: 0 }}
                                                                >
                                                                    {isAuthenticating ? (
                                                                        <>
                                                                            <SpinnerIcon size={18} />
                                                                            Signing in
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Swords size={18} />
                                                                            Find online match
                                                                        </>
                                                                    )}
                                                                </SubmitButton>
                                                            </ModeSection>
                                                        </form>
                                                        <SectionOr>or</SectionOr>
                                                        <ModeSection $variant="solo" aria-label="Solo vs AI">
                                                            <ModeLabel $variant="solo">
                                                                <Bot size={14} aria-hidden="true" />
                                                                Solo / Practice
                                                            </ModeLabel>
                                                            <ModeBlurb>
                                                                Instant game vs the AI. No queue. Your rating is unchanged.
                                                            </ModeBlurb>
                                                            <FormGroup>
                                                                <Label htmlFor="mm-ai-difficulty-guest2">AI difficulty</Label>
                                                                <InputWrap>
                                                                    <Select
                                                                        id="mm-ai-difficulty-guest2"
                                                                        value={aiDifficulty}
                                                                        onChange={(e) => setAiDifficulty(e.target.value)}
                                                                        disabled={isAuthenticating}
                                                                    >
                                                                        <option value="easy">Easy (training)</option>
                                                                        <option value="normal">Normal</option>
                                                                        <option value="hard">Hard</option>
                                                                    </Select>
                                                                </InputWrap>
                                                            </FormGroup>
                                                            <SoloActionButton
                                                                type="button"
                                                                onClick={beginSoloMatch}
                                                                disabled={isAuthenticating}
                                                                aria-label="Start solo match versus AI"
                                                            >
                                                                {isAuthenticating ? (
                                                                    <>
                                                                        <SpinnerIcon size={18} />
                                                                        Signing in
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Bot size={18} />
                                                                        Play vs AI
                                                                    </>
                                                                )}
                                                            </SoloActionButton>
                                                        </ModeSection>
                                                    </>
                                                )}
                                            </>
                                        )}

                                        {isLoggedIn && !showAccountForm && (
                                            <>
                                                {preferredMode === "solo" ? (
                                                    <>
                                                        <ModeSection $variant="solo" aria-label="Solo vs AI">
                                                            <ModeLabel $variant="solo">
                                                                <Bot size={14} aria-hidden="true" />
                                                                Solo / Practice
                                                            </ModeLabel>
                                                            <ModeBlurb>
                                                                Instant game vs the AI. No queue. Your rating is unchanged.
                                                            </ModeBlurb>
                                                            <FormGroup>
                                                                <Label htmlFor="mm-ai-difficulty">AI difficulty</Label>
                                                                <InputWrap>
                                                                    <Select
                                                                        id="mm-ai-difficulty"
                                                                        value={aiDifficulty}
                                                                        onChange={(e) => setAiDifficulty(e.target.value)}
                                                                        disabled={isAuthenticating}
                                                                    >
                                                                        <option value="easy">Easy (training)</option>
                                                                        <option value="normal">Normal</option>
                                                                        <option value="hard">Hard</option>
                                                                    </Select>
                                                                </InputWrap>
                                                            </FormGroup>
                                                            <SoloActionButton
                                                                type="button"
                                                                onClick={() => startSoloVsAi(getStoredAuth().token || authToken)}
                                                                disabled={isAuthenticating}
                                                            >
                                                                {isAuthenticating ? (
                                                                    <>
                                                                        <SpinnerIcon size={18} />
                                                                        Starting
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Bot size={18} />
                                                                        Play vs AI
                                                                    </>
                                                                )}
                                                            </SoloActionButton>
                                                        </ModeSection>
                                                        <SectionOr>or</SectionOr>
                                                        <ModeSection $variant="online" aria-label="Online matchmaking">
                                                            <ModeLabel $variant="online">
                                                                <Users size={14} aria-hidden="true" />
                                                                Online
                                                            </ModeLabel>
                                                            <ModeBlurb>Queue for a rated match vs another player.</ModeBlurb>
                                                            <SubmitButton
                                                                className="find-match-button"
                                                                type="button"
                                                                onClick={() => startMatchmaking(getStoredAuth().token || authToken)}
                                                                disabled={isAuthenticating}
                                                                style={{ marginTop: 0 }}
                                                            >
                                                                <Swords size={18} />
                                                                Find online match
                                                            </SubmitButton>
                                                            <TertiaryLink
                                                                type="button"
                                                                onClick={() => {
                                                                    setShowAccountForm(true);
                                                                    setError("");
                                                                    setPasswordError("");
                                                                }}
                                                            >
                                                                Use a different battle name…
                                                            </TertiaryLink>
                                                        </ModeSection>
                                                    </>
                                                ) : (
                                                    <>
                                                        <ModeSection $variant="online" aria-label="Online matchmaking">
                                                            <ModeLabel $variant="online">
                                                                <Users size={14} aria-hidden="true" />
                                                                Online
                                                            </ModeLabel>
                                                            <ModeBlurb>Queue for a rated match vs another player.</ModeBlurb>
                                                            <SubmitButton
                                                                className="find-match-button"
                                                                type="button"
                                                                onClick={() => startMatchmaking(getStoredAuth().token || authToken)}
                                                                disabled={isAuthenticating}
                                                                style={{ marginTop: 0 }}
                                                            >
                                                                <Swords size={18} />
                                                                Find online match
                                                            </SubmitButton>
                                                            <TertiaryLink
                                                                type="button"
                                                                onClick={() => {
                                                                    setShowAccountForm(true);
                                                                    setError("");
                                                                    setPasswordError("");
                                                                }}
                                                            >
                                                                Use a different battle name…
                                                            </TertiaryLink>
                                                        </ModeSection>
                                                        <SectionOr>or</SectionOr>
                                                        <ModeSection $variant="solo" aria-label="Solo vs AI">
                                                            <ModeLabel $variant="solo">
                                                                <Bot size={14} aria-hidden="true" />
                                                                Solo / Practice
                                                            </ModeLabel>
                                                            <ModeBlurb>
                                                                Instant game vs the AI. No queue. Your rating is unchanged.
                                                            </ModeBlurb>
                                                            <FormGroup>
                                                                <Label htmlFor="mm-ai-difficulty-li">AI difficulty</Label>
                                                                <InputWrap>
                                                                    <Select
                                                                        id="mm-ai-difficulty-li"
                                                                        value={aiDifficulty}
                                                                        onChange={(e) => setAiDifficulty(e.target.value)}
                                                                        disabled={isAuthenticating}
                                                                    >
                                                                        <option value="easy">Easy (training)</option>
                                                                        <option value="normal">Normal</option>
                                                                        <option value="hard">Hard</option>
                                                                    </Select>
                                                                </InputWrap>
                                                            </FormGroup>
                                                            <SoloActionButton
                                                                type="button"
                                                                onClick={() => startSoloVsAi(getStoredAuth().token || authToken)}
                                                                disabled={isAuthenticating}
                                                            >
                                                                {isAuthenticating ? (
                                                                    <>
                                                                        <SpinnerIcon size={18} />
                                                                        Starting
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Bot size={18} />
                                                                        Play vs AI
                                                                    </>
                                                                )}
                                                            </SoloActionButton>
                                                        </ModeSection>
                                                    </>
                                                )}
                                            </>
                                        )}

                                        <div style={{ textAlign: "center" }}>
                                            <TutorialLink type="button" onClick={openTutorial}>
                                                How to Play?
                                            </TutorialLink>
                                        </div>
                                    </>
                                )}
                            </DrawerInner>
                        </Drawer>
                    </>
                )}
            </AnimatePresence>

            <Tutorial
                isOpen={showTutorial}
                onClose={() => setShowTutorial(false)}
                tutorialType="main"
            />
        </>
    );
};

export default MyForm;
